import React from 'react';
// @ts-ignore
import styles from 'InvoiceExportHistory.module.scss';
import useTranslation from '../../../../../../Hooks/useTranslation';
import {
	ExportLogFormat,
	ExportLogStatus,
	ExportLogType,
	useLazyExportLogsQuery,
} from '../../../../../../graphql/graphql';
import useFlatData from '../../../../../Base/Hooks/useFlatData';
import { useAppContext } from '../../../../../../Services/AppContext';
import useInvoiceExportHistory from '../../../../Hooks/useInvoiceExportHistory';
import OverlayLoading from '../../../../../../Components/OverlayLoading';
import ScrollContainer from '../../../../../Base/Components/ScrollContainer';
import Loading from '../../../../../../Components/Loading';
import InfiniteScroll from 'react-infinite-scroll-component';
import FormattedMomentDate, {
	DateFormat,
} from '../../../../../Base/Components/FormattedMomentDate/FormattedMomentDate';

export interface Props {
	isSuperAdmin?: boolean;
}

const WRAPPER_ID = 'invoiceExportHistoryWrapper';

const FORMAT_TRANSLATIONS: Record<ExportLogFormat, string> = {
	[ExportLogFormat.Pdf]: 'component.invoiceExportRequest.formatOptions.PDF',
	[ExportLogFormat.Isdoc]: 'component.invoiceExportRequest.formatOptions.ISDOC',
	[ExportLogFormat.Pohoda]:
		'component.invoiceExportRequest.formatOptions.POHODA',
};

const STATUS_TRANSLATIONS: Record<ExportLogStatus, string> = {
	[ExportLogStatus.Pending]:
		'component.invoiceExportHistory.statusOption.pending',
	[ExportLogStatus.Success]:
		'component.invoiceExportHistory.statusOption.success',
	[ExportLogStatus.Failed]:
		'component.invoiceExportHistory.statusOption.failed',
};

const STATUS_COLORS: Record<ExportLogStatus, string> = {
	[ExportLogStatus.Pending]: '#f28a11',
	[ExportLogStatus.Success]: '#619612',
	[ExportLogStatus.Failed]: '#d81616',
};

interface InvoiceExportHistoryItem {
	id: string;
	createdById: string;
	createdBy: {
		profile: {
			fullName: string;
		};
	};
	status: ExportLogStatus;
	type: ExportLogType;
	format: ExportLogFormat;
	createdAt: string;
	extra: {
		dateFrom?: string;
		dateTo?: string;
		recipients?: string;
	};
}

const InvoiceExportHistory: React.FC<Props> = ({ isSuperAdmin }) => {
	const { t } = useTranslation();
	const { workplaceId } = useAppContext();

	const [getInvoiceExportHistoryQuery, { data, isLoading }] =
		useLazyExportLogsQuery();
	const { getInvoiceExportHistory, getNextPage, page, setPage } =
		useInvoiceExportHistory(getInvoiceExportHistoryQuery);
	const {
		flatData,
		hasMoreData,
		loadData,
		isLoading: isFlatDataLoading,
	} = useFlatData(
		getInvoiceExportHistory,
		{
			data: data?.exportLogs?.data,
			totalPages: data?.exportLogs?.meta?.totalPages,
			page: data?.exportLogs?.meta?.page,
		},
		page,
		setPage,
		{
			workplaceId,
			type: ExportLogType.Invoicing,
		},
	);

	return (
		<div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
			{isFlatDataLoading && <OverlayLoading />}
			<ScrollContainer wrapperId={WRAPPER_ID}>
				<InfiniteScroll
					dataLength={flatData.length}
					refreshFunction={loadData}
					next={getNextPage}
					hasMore={hasMoreData}
					loader={<Loading />}
					scrollableTarget={WRAPPER_ID}>
					<div className={styles.wrapper}>
						<table>
							<thead>
								<tr>
									<th>{t('component.invoiceExportHistory.date')}</th>
									<th>{t('component.invoiceExportHistory.exportedBy')}</th>
									<th>{t('component.invoiceExportHistory.dateRange')}</th>
									<th>{t('component.invoiceExportHistory.format')}</th>
									<th>{t('component.invoiceExportHistory.email')}</th>
									<th>{t('component.invoiceExportHistory.status')}</th>
								</tr>
							</thead>
							<tbody>
								{flatData?.map((item: InvoiceExportHistoryItem, index) => (
									<tr key={index} className={styles.item}>
										<td>
											<FormattedMomentDate
												date={item.createdAt}
												format={DateFormat.DATE_TIME}
											/>
										</td>
										<td>{item.createdBy.profile.fullName}</td>
										<td>
											{item.extra?.dateFrom && (
												<FormattedMomentDate
													date={item.extra?.dateFrom}
													format={DateFormat.DATE}
												/>
											)}{' '}
											-{' '}
											{item.extra?.dateTo && (
												<FormattedMomentDate
													date={item.extra?.dateTo}
													format={DateFormat.DATE}
												/>
											)}
										</td>
										<td>{t(FORMAT_TRANSLATIONS[item.format])}</td>
										<td>{item.extra?.recipients}</td>
										<td
											className={styles.status}
											style={{ color: STATUS_COLORS[item.status] }}>
											{t(STATUS_TRANSLATIONS[item.status])}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</InfiniteScroll>
			</ScrollContainer>
		</div>
	);
};

export default InvoiceExportHistory;
